import React, { useState, useRef, useEffect } from 'react';

interface WebRTCTestProps {
  selectedUser?: string;
  selectedProfession?: string;
  currentUserId?: string;
}

interface SessionMetrics {
  sessionId: string | null;
  startTime: Date | null;
  messageCount: number;
  functionCalls: number;
  quality: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  audioInputTokens: number;
  audioOutputTokens: number;
  textInputTokens: number;
  textOutputTokens: number;
  totalCostUSD: number;
  totalCostEUR: number;
}

interface RecordingState {
  isRecording: boolean;
  recordingStartTime: Date | null;
  duration: number;
  audioChunks: Blob[];
}

const WebRTCTest: React.FC<WebRTCTestProps> = ({ selectedUser, selectedProfession, currentUserId }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Pasiruošęs prisijungti');
  const [messages, setMessages] = useState<string[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [sessionMetrics, setSessionMetrics] = useState<SessionMetrics>({
    sessionId: null,
    startTime: null,
    messageCount: 0,
    functionCalls: 0,
    quality: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    audioInputTokens: 0,
    audioOutputTokens: 0,
    textInputTokens: 0,
    textOutputTokens: 0,
    totalCostUSD: 0,
    totalCostEUR: 0
  });
  const [conversationHistory, setConversationHistory] = useState<Array<{
    role: string;
    content: string;
    timestamp: string;
    confidence?: number;
  }>>([]);
  const [recordingState, setRecordingState] = useState<RecordingState>({
    isRecording: false,
    recordingStartTime: null,
    duration: 0,
    audioChunks: []
  });

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const mixedStreamRef = useRef<MediaStream | null>(null);
  const currentCallIdRef = useRef<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio element
  useEffect(() => {
    // Create audio element for remote stream playback
    if (!audioElementRef.current) {
      audioElementRef.current = new Audio();
      audioElementRef.current.autoplay = true;
      audioElementRef.current.volume = 1.0;
      console.log('🔊 Audio element created for remote playback');
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clean up audio element on unmount
      if (audioElementRef.current) {
        try {
          audioElementRef.current.pause();
          audioElementRef.current.srcObject = null;
          audioElementRef.current = null;
        } catch (e) {
          console.warn('Error cleaning up audio element on unmount:', e);
        }
      }

      // Disconnect if still connected
      if (isConnected || isConnecting) {
        disconnect();
      }
    };
  }, []);

  // Update session quality based on metrics
  useEffect(() => {
    if (sessionMetrics.messageCount > 0 && sessionMetrics.startTime) {
      const sessionDuration = Date.now() - sessionMetrics.startTime.getTime();
      const avgResponseTime = sessionDuration / sessionMetrics.messageCount;

      // Calculate quality score based on response time and engagement
      let quality = 0.5; // Base score

      // Response time factor (lower is better)
      if (avgResponseTime < 2000) quality += 0.2;
      else if (avgResponseTime < 5000) quality += 0.1;

      // Message count factor (engagement)
      if (sessionMetrics.messageCount >= 3) quality += 0.1;
      if (sessionMetrics.messageCount >= 5) quality += 0.1;

      // Function usage factor
      if (sessionMetrics.functionCalls > 0) quality += 0.1;

      updateSessionMetrics({ quality: Math.min(1.0, quality) });
    }
  }, [sessionMetrics.messageCount, sessionMetrics.functionCalls]);

  const addMessage = (message: string) => {
    setMessages(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const updateSessionMetrics = (updates: Partial<SessionMetrics>) => {
    setSessionMetrics(prev => ({ ...prev, ...updates }));
  };

  // Handle function calls from AI
  const handleFunctionCall = async (functionName: string, args: string, callId: string, dataChannel: RTCDataChannel) => {
    console.log(`🔧 Executing function: ${functionName}`, args);
    console.log(`🔍 Function call context:`, { selectedUser, selectedProfession, functionName });
    
    try {
      // Parse arguments
      const parsedArgs = JSON.parse(args);
      
      // Determine profession - use stogdengys if not selected since that's what tools were loaded for
      const profession = selectedProfession || 'stogdengys';
      console.log(`🎯 Using profession: ${profession}`);
      
      // Call our backend API to execute the function with timeout
      console.log(`📡 Calling backend function API...`);
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch('/api/test/function-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          functionName,
          args: parsedArgs,
          userId: selectedUser,
          profession: profession
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      console.log(`📡 Backend response status: ${response.status}`);
      
      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log(`✅ Function result:`, result);
      
      // Check if function execution was successful
      if (!result.success && result.error) {
        console.warn(`⚠️ Function returned error: ${result.error}`);
      }
      
      // Send function result back to AI
      const functionOutput = {
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify(result)
        }
      };
      
      dataChannel.send(JSON.stringify(functionOutput));
      
      // Trigger AI response
      dataChannel.send(JSON.stringify({
        type: 'response.create'
      }));
      
      addMessage(`✅ Funkcija ${functionName} įvykdyta sėkmingai`);
      
    } catch (error) {
      console.error(`❌ Function execution failed:`, error);
      console.error(`❌ Function call details:`, { functionName, args, callId });
      
      // Send error back to AI
      const errorOutput = {
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify({ error: 'Function execution failed' })
        }
      };
      
      dataChannel.send(JSON.stringify(errorOutput));
      dataChannel.send(JSON.stringify({ type: 'response.create' }));
      
      addMessage(`❌ Funkcijos ${functionName} klaida`);
    }
  };

  const addConversationEntry = (role: string, content: string, confidence?: number) => {
    const entry = {
      role,
      content,
      timestamp: new Date().toISOString(),
      ...(confidence !== undefined && { confidence })
    };
    setConversationHistory(prev => [...prev, entry]);

    // Update message count
    if (role === 'user' || role === 'assistant') {
      updateSessionMetrics({ messageCount: sessionMetrics.messageCount + 1 });
    }
  };

  // Initialize WebSocket connection for session management
  const initializeWebSocket = () => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}`;

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log('📡 WebSocket connected for session management');
      addMessage('📡 Session management pripojigts');

      // Subscribe to metrics
      wsRef.current?.send(JSON.stringify({
        type: 'subscribe_metrics'
      }));
    };

    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('📡 WebSocket message:', data);

        switch (data.type) {
          case 'session_created':
            updateSessionMetrics({ sessionId: data.sessionId });
            addMessage(` Sesija sukurta: ${data.sessionId.substring(0, 8)}...`);
            break;

          case 'session_metrics':
            if (data.metrics) {
              updateSessionMetrics({
                messageCount: data.metrics.messageCount || sessionMetrics.messageCount,
                functionCalls: data.metrics.functionCalls || sessionMetrics.functionCalls,
                quality: data.metrics.quality || sessionMetrics.quality
              });
            }
            break;

          case 'metrics_update':
            addMessage(` Aktyvios sesijos: ${data.data.activeSessions || 0}`);
            break;

          case 'realtime_event':
            if (data.event.type === 'speech_started') {
              addMessage('🎤 Kalbėjimo pradžia');
            } else if (data.event.type === 'speech_stopped') {
              addMessage('🛑 Kalbėjimo pabaiga');
            } else if (data.event.type === 'conversation_item_created') {
              if (data.event.item && data.event.item.content) {
                addConversationEntry(
                  data.event.item.role || 'assistant',
                  data.event.item.content,
                  data.event.confidence
                );
              }
            }
            break;
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };

    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      addMessage(' WebSocket klaida');
    };

    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
      addMessage('🔌 WebSocket atjungts');
    };
  };

  const connectToAI = async () => {
    if (isConnecting || isConnected) return;

    setIsConnecting(true);
    setStatus('Gaunamas žetonas...');
    addMessage('Pradedamas prisijungimas...');

    // Initialize session tracking
    setSessionMetrics(prev => ({
      ...prev,
      startTime: new Date(),
      messageCount: 0,
      functionCalls: 0,
      quality: 0
    }));
    setConversationHistory([]);

    try {
      // Initialize WebSocket for session management
      initializeWebSocket();

      // Get ephemeral token with user context
      const tokenPayload: any = {
        voice: 'shimmer'
      };

      console.log('🔍 WebRTC Debug - selectedUser:', selectedUser, 'selectedProfession:', selectedProfession);

      if (selectedUser && selectedUser !== '') {
        tokenPayload.userId = selectedUser;
        console.log(' Adding userId to payload:', selectedUser);
      } else if (selectedProfession && selectedProfession !== '') {
        tokenPayload.profession = selectedProfession;
        console.log(' Adding profession to payload:', selectedProfession);
      } else {
        console.log(' No user or profession selected');
      }

      const tokenResponse = await fetch('/api/realtime/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(tokenPayload)
      });

      if (!tokenResponse.ok) {
        throw new Error(`Token request failed: ${tokenResponse.status} ${tokenResponse.statusText}`);
      }

      const { token, model, prompt, tools } = await tokenResponse.json();
      console.log('Token received:', token.substring(0, 20) + '...', 'Model:', model);
      setStatus(`Žetonas gautas. Modelis: ${model}`);
      addMessage(`Žetonas gautas modeliui: ${model}`);

      if (prompt) {
        addMessage(`Individualus promptas įkeltas ${selectedUser ? 'vartotojui' : 'profesijai'}`);
      }

      // Create peer connection
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
      });
      pcRef.current = pc;

      // Get user media with optimal settings for recording
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000,
          channelCount: 1
        }
      });
      localStreamRef.current = stream;
      stream.getTracks().forEach(track => pc.addTrack(track, stream));
      addMessage('Mikrofono prieiga suteikta');

      // Generate call ID for recording
      currentCallIdRef.current = crypto.randomUUID();

      // Handle remote audio track for recording AND playback
      pc.ontrack = (event) => {
        console.log('Remote track received:', event.track.kind, event.streams);

        if (event.track.kind === 'audio' && event.streams[0]) {
          const remoteStream = event.streams[0];
          remoteStreamRef.current = remoteStream;

          // CRITICAL: Connect remote stream to audio element for playback
          if (audioElementRef.current) {
            try {
              // Clear any existing srcObject first
              if (audioElementRef.current.srcObject) {
                audioElementRef.current.srcObject = null;
              }

              audioElementRef.current.srcObject = remoteStream;
              audioElementRef.current.play().then(() => {
                console.log('🔊 Remote audio playback started');
                addMessage('🔊 AI balso atkūrimas pradėtas');
              }).catch((e) => {
                console.error(' Audio playback failed:', e);
                addMessage(' AI balso atkūrimo klaida');
              });
            } catch (error) {
              console.error('Error setting up audio playback:', error);
              addMessage(' Klaida nustatant audio atkūrimą');
            }
          }

          // Start recording with both streams
          if (localStreamRef.current) {
            console.log(' Starting recording with both streams');
            startRecording(localStreamRef.current, remoteStream);
          }
        }
      };

      // Create data channel
      const dataChannel = pc.createDataChannel('oai-events', { ordered: true });
      dataChannelRef.current = dataChannel;

      dataChannel.addEventListener('open', () => {
        console.log('Data channel opened - sending config immediately');
        setStatus('Prisijungta prie AI');
        setIsConnected(true);
        setIsConnecting(false);
        addMessage(' Prisijungta prie AI – pradėkite kalbėti!');

        // Build session config exactly like dental voice app
        const sessionConfig: any = {
          modalities: ['text', 'audio'],
          voice: 'shimmer',
          instructions: prompt || 'Jūs esate profesionalus asistentas. Atsakykite lietuviškai, trumpai ir aiškiai.',
          temperature: 0.8,
          input_audio_transcription: {
            model: 'gpt-4o-mini-transcribe'
          },
          input_audio_format: 'pcm16',
          output_audio_format: 'pcm16',
          turn_detection: {
            type: 'server_vad',
            threshold: 0.6,
            prefix_padding_ms: 300,
            silence_duration_ms: 1000
          }
        };

        // Add tools if available
        if (tools && tools.length > 0) {
          sessionConfig.tools = tools;
          sessionConfig.tool_choice = 'auto';
          console.log(' Adding tools to session:', tools);
        }

        const configMessage = {
          type: 'session.update',
          session: sessionConfig
        };

        console.log('📤 Sending session update:', JSON.stringify(configMessage, null, 2));
        dataChannel.send(JSON.stringify(configMessage));
        addMessage('Sesijos konfigūracija atnaujinta');

        if (tools && tools.length > 0) {
          addMessage(` Pridėti ${tools.length} įrankiai profesijai`);
        }
      });

      dataChannel.addEventListener('message', (e) => {
        const msg = JSON.parse(e.data);
        console.log('Message from AI:', msg);

        // Handle different message types from OpenAI Realtime API
        console.log('🔍 Processing message type:', msg.type);
        switch (msg.type) {
          case 'error':
            addMessage(` Klaida: ${msg.error.message}`);
            break;

          case 'session.updated':
            console.log(' Session updated event received!', msg);
            addMessage(' Sesija atnaujinta su individualiais nustatymais');
            // Now trigger the greeting after session is fully configured
            setTimeout(() => {
              if (dataChannel.readyState === 'open') {
                const greetingTrigger = {
                  type: 'response.create',
                  response: {
                    modalities: ['text', 'audio']
                  }
                };
                console.log('📤 Sending greeting trigger:', greetingTrigger);
                dataChannel.send(JSON.stringify(greetingTrigger));
                addMessage('🎤 AI asistentas pradeda pokalbį...');
              } else {
                console.error(' DataChannel not open when trying to send greeting');
              }
            }, 100);
            break;

          case 'response.function_call_arguments.done':
            // Handle function call execution
            console.log('🔧 Function call received:', msg.name, msg.arguments);
            addMessage(` AI kviečia funkciją: ${msg.name}`);
            updateSessionMetrics({ functionCalls: sessionMetrics.functionCalls + 1 });
            
            // Execute the function and send result back
            handleFunctionCall(msg.name, msg.arguments, msg.call_id, dataChannel);
            break;

          case 'response.function_call_output':
            addMessage(` Įrankis įvykdytas: ${msg.name}`);
            break;

          case 'conversation.item.created':
            if (msg.item) {
              if (msg.item.type === 'function_call') {
                addMessage(` AI kviečia funkciją: ${msg.item.name}`);
                updateSessionMetrics({ functionCalls: sessionMetrics.functionCalls + 1 });
              } else if (msg.item.type === 'message' && msg.item.content) {
                // Add to conversation history
                const content = Array.isArray(msg.item.content)
                  ? msg.item.content.map((c: any) => c.text || c.transcript || '').join(' ')
                  : msg.item.content;
                addConversationEntry(msg.item.role || 'assistant', content);
              }
            }
            break;

          case 'conversation.item.input_audio_transcription.delta':
            // Real-time transcription updates from new gpt-realtime model
            // Just accumulate silently, will show complete transcript on 'completed' event
            break;

          case 'conversation.item.input_audio_transcription.completed':
            if (msg.transcript) {
              addConversationEntry('user', msg.transcript, msg.confidence);
              addMessage(`🎤 Vartotojas: ${msg.transcript.substring(0, 50)}${msg.transcript.length > 50 ? '...' : ''}`);
            }
            break;

          case 'response.audio.delta':
            // Audio is handled via WebRTC audio track, not data channel
            addMessage('🔊 AI audio data received');
            break;

          case 'response.audio_transcript.delta':
          case 'response.audio_transcript.done':
            if (msg.transcript || msg.delta) {
              const text = msg.transcript || msg.delta;
              // Update ongoing AI response
              addMessage(`🔊 AI: ${text.substring(0, 50)}${text.length > 50 ? '...' : ''}`);
            }
            break;

          case 'response.done':
            // Response completed, calculate quality score AND token usage
            const avgResponseTime = Date.now() - (sessionMetrics.startTime?.getTime() || Date.now());
            const quality = Math.min(1.0, Math.max(0.3, 1.0 - (avgResponseTime / 10000))); // Simple quality calculation

            // TRACK TOKEN USAGE AND COSTS
            if (msg.response && msg.response.usage) {
              const usage = msg.response.usage;
              console.log('💰 Token Usage:', usage);

              // Extract token counts
              const inputTokens = usage.input_tokens || 0;
              const outputTokens = usage.output_tokens || 0;
              const audioInput = usage.input_token_details?.audio || 0;
              const audioOutput = usage.output_token_details?.audio || 0;
              const textInput = inputTokens - audioInput;
              const textOutput = outputTokens - audioOutput;

              // Calculate costs (OpenAI Realtime API pricing)
              const textInputCost = textInput * 0.000004;    // $4/1M tokens
              const textOutputCost = textOutput * 0.000016;  // $16/1M tokens
              const audioInputCost = audioInput * 0.000032;  // $32/1M tokens
              const audioOutputCost = audioOutput * 0.000064; // $64/1M tokens

              const totalCostUSD = textInputCost + textOutputCost + audioInputCost + audioOutputCost;
              const totalCostEUR = totalCostUSD * 0.92; // USD to EUR conversion

              // Update cumulative metrics using functional update to avoid stale state
              setSessionMetrics(prev => {
                const updated = {
                  ...prev,
                  quality,
                  totalInputTokens: prev.totalInputTokens + inputTokens,
                  totalOutputTokens: prev.totalOutputTokens + outputTokens,
                  audioInputTokens: prev.audioInputTokens + audioInput,
                  audioOutputTokens: prev.audioOutputTokens + audioOutput,
                  textInputTokens: prev.textInputTokens + textInput,
                  textOutputTokens: prev.textOutputTokens + textOutput,
                  totalCostUSD: prev.totalCostUSD + totalCostUSD,
                  totalCostEUR: prev.totalCostEUR + totalCostEUR
                };
                console.log('📈 Updated session metrics:', {
                  totalCost: `€${updated.totalCostEUR.toFixed(4)}`,
                  totalTokens: updated.totalInputTokens + updated.totalOutputTokens
                });
                return updated;
              });

              addMessage(`💰 Šios atsakymo kaina: €${totalCostEUR.toFixed(4)} ($${totalCostUSD.toFixed(4)})`);
              addMessage(`📊 Tokenai: ${inputTokens} įėjimo + ${outputTokens} išėjimo`);
            }
            break;

          case 'input_audio_buffer.speech_started':
            addMessage('🎤 Kalbėjimo pradžia');
            break;

          case 'input_audio_buffer.speech_stopped':
            addMessage('🛑 Kalbėjimo pabaiga');
            break;

          case 'output_audio_buffer.started':
            addMessage('🔊 AI: output_audio_buffer.started');
            break;

          case 'response.output_item.added':
            if (msg.item && msg.item.type === 'message') {
              addMessage('📡 AI: response.output_item.added');
            }
            break;

          case 'response.content_part.added':
            addMessage('🔊 AI: response.content_part.added');
            break;

          case 'response.audio.done':
            if (msg.transcript) {
              addMessage(`🔊 AI: ${msg.transcript.substring(0, 100)}${msg.transcript.length > 100 ? '...' : ''}`);
              addConversationEntry('assistant', msg.transcript);
            }
            break;

          default:
            console.log(' Unhandled message type:', msg.type, msg);
            addMessage(`📡 AI: ${msg.type}`);

            // Special handling for session.updated in default case
            if (msg.type === 'session.updated') {
              console.log(' Session updated detected in default case!');
              setTimeout(() => {
                if (dataChannel.readyState === 'open') {
                  const greetingTrigger = {
                    type: 'response.create',
                    response: {
                      modalities: ['text', 'audio']
                    }
                  };
                  console.log('📤 Sending greeting trigger from default case:', greetingTrigger);
                  dataChannel.send(JSON.stringify(greetingTrigger));
                  addMessage('🎤 AI asistentas pradeda pokalbį...');
                } else {
                  console.error(' DataChannel not open when trying to send greeting');
                }
              }, 100);
            }
            break;
        }
      });

      dataChannel.addEventListener('close', () => {
        addMessage(' Duomenų kanalas uždarytas');
        setIsConnected(false);
      });

      dataChannel.addEventListener('error', (error) => {
        console.error('Data channel error:', error);
        addMessage(' Duomenų kanalo klaida');
      });

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      addMessage('WebRTC pasiūlymas sukurtas');

      // Send offer to OpenAI
      const sdpResponse = await fetch(`https://api.openai.com/v1/realtime`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/sdp'
        },
        body: offer.sdp || ''
      });

      if (!sdpResponse.ok) {
        throw new Error(`SDP exchange failed: ${sdpResponse.status} ${sdpResponse.statusText}`);
      }

      const answer = await sdpResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      addMessage('WebRTC prisijungimas nustatytas');

      setStatus('Prisijungta! Kalbėkite į mikrofoną.');

    } catch (error) {
      console.error('Connection error:', error);
      setStatus(`Klaida: ${(error as Error).message}`);
      addMessage(` Prisijungimas nepavyko: ${(error as Error).message}`);
      setIsConnecting(false);
      setIsConnected(false);
    }
  };

  const disconnect = async () => {
    try {
      // Stop recording first
      await stopRecording();

      // Save conversation and analytics before disconnecting
      if (sessionMetrics.sessionId && conversationHistory.length > 0) {
        addMessage(' Išsaugomas pokalbis...');

        const sessionEnd = new Date();
        const duration = sessionMetrics.startTime
          ? Math.floor((sessionEnd.getTime() - sessionMetrics.startTime.getTime()) / 1000)
          : 0;

        // Send session end event to WebSocket
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'session_end',
            sessionId: sessionMetrics.sessionId,
            conversationHistory,
            duration,
            metrics: {
              messageCount: sessionMetrics.messageCount,
              functionCalls: sessionMetrics.functionCalls,
              quality: sessionMetrics.quality
            }
          }));
        }

        // Also try to save via API endpoint
        try {
          await fetch('/api/conversations/save', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              sessionId: sessionMetrics.sessionId,
              conversationHistory,
              duration,
              metrics: sessionMetrics
            })
          });
          addMessage(' Pokalbis išsaugotas');
        } catch (saveError) {
          console.warn('Failed to save conversation via API:', saveError);
          addMessage(' Pokalbio išsaugojimo klaida');
        }
      }
    } catch (error) {
      console.error('Error during disconnect:', error);
      addMessage(' Atsijungimo klaida');
    }

    // Clean up audio element first
    if (audioElementRef.current) {
      try {
        audioElementRef.current.pause();
        audioElementRef.current.srcObject = null;
      } catch (e) {
        console.warn('Error cleaning up audio element:', e);
      }
    }

    // Clean up streams
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      localStreamRef.current = null;
    }

    if (remoteStreamRef.current) {
      remoteStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      remoteStreamRef.current = null;
    }

    // Close connections
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    setIsConnecting(false);
    setStatus('Atsijungta');
    addMessage('🔌 Atsijungta nuo AI');
  };

  const clearMessages = () => {
    setMessages([]);
  };

  // Recording functions (based on dental voice app implementation)
  const startRecording = (localStream: MediaStream, remoteStream: MediaStream) => {
    try {
      console.log(' Starting audio recording...');
      addMessage('📼 Įrašymas pradėtas');

      // Create audio context for mixing
      audioContextRef.current = new AudioContext();

      // Create sources for both streams
      const localSource = audioContextRef.current.createMediaStreamSource(localStream);
      const remoteSource = audioContextRef.current.createMediaStreamSource(remoteStream);

      // Create destination for mixed audio
      const destination = audioContextRef.current.createMediaStreamDestination();

      // Connect both sources to destination
      localSource.connect(destination);
      remoteSource.connect(destination);

      // Store mixed stream
      mixedStreamRef.current = destination.stream;

      // Create media recorder with the mixed stream
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      mediaRecorderRef.current = new MediaRecorder(mixedStreamRef.current, {
        mimeType,
        audioBitsPerSecond: 128000
      });

      // Handle data available
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordingState(prev => ({
            ...prev,
            audioChunks: [...prev.audioChunks, event.data]
          }));
          console.log(` Audio chunk received: ${event.data.size} bytes`);
        }
      };

      // Handle recording stop
      mediaRecorderRef.current.onstop = async () => {
        console.log(' Recording stopped, saving...');
        await saveRecording();
      };

      // Handle errors
      mediaRecorderRef.current.onerror = (event: any) => {
        console.error(' MediaRecorder error:', event);
        addMessage(' Įrašymo klaida');
      };

      // Start recording
      setRecordingState(prev => ({
        ...prev,
        isRecording: true,
        recordingStartTime: new Date(),
        audioChunks: []
      }));

      mediaRecorderRef.current.start(1000); // 1 second chunks
      console.log(' Audio recording started successfully');

    } catch (error) {
      console.error(' Failed to start recording:', error);
      addMessage(' Nepavyko pradėti įrašymo');
    }
  };

  const stopRecording = async (): Promise<void> => {
    console.log('🛑 Stopping audio recording...');
    addMessage('🛑 Stabdomas įrašymas...');

    // If MediaRecorder is inactive or doesn't exist, try to save what we have
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
      console.log('⚠️ MediaRecorder already inactive, attempting to save existing chunks...');
      await saveRecording();
      return;
    }

    return new Promise((resolve) => {
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.onstop = async () => {
          await saveRecording();
          resolve();
        };
        mediaRecorderRef.current.stop();
      } else {
        resolve();
      }
    });
  };

  const saveRecording = async (): Promise<void> => {
    console.log(' saveRecording called');

    if (recordingState.audioChunks.length === 0) {
      console.log(' No audio data to save');
      return;
    }

    try {
      // Create blob from chunks
      const audioBlob = new Blob(recordingState.audioChunks, { type: 'audio/webm' });
      console.log(`📼 Audio blob created: ${(audioBlob.size / 1024).toFixed(2)}KB`);

      // Convert to base64 for storage
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onloadend = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data:audio/webm;base64, prefix
        };
        reader.readAsDataURL(audioBlob);
      });

      const audioBase64 = await base64Promise;

      // Calculate duration
      const durationSeconds = recordingState.recordingStartTime
        ? Math.floor((Date.now() - recordingState.recordingStartTime.getTime()) / 1000)
        : 0;

      // Prepare recording data
      const recordingData = {
        call_id: currentCallIdRef.current,
        session_id: sessionMetrics.sessionId,
        vartotojo_id: currentUserId || selectedUser || null,
        audio_data: audioBase64,
        format: 'webm',
        duration_seconds: durationSeconds,
        size_bytes: audioBlob.size,
        customer_name: null, // Could be extracted from conversation
        customer_phone: null,
        transcript: conversationHistory.map(h => `${h.role}: ${h.content}`).join('\n'),
        recorded_at: recordingState.recordingStartTime?.toISOString() || new Date().toISOString()
      };

      // Save to database via API
      const response = await fetch('/api/webrtc/save-recording', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(recordingData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Recording saved successfully:', result.id);
        addMessage(`✅ Įrašas išsaugotas (${(audioBlob.size / 1024).toFixed(2)}KB)`);
      } else {
        const errorText = await response.text();
        console.error('❌ Failed to save recording:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        addMessage(`❌ Nepavyko išsaugoti įrašo (${response.status})`);
      }

      // Clean up
      setRecordingState({
        isRecording: false,
        recordingStartTime: null,
        duration: 0,
        audioChunks: []
      });

      // Clean up audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }

    } catch (error) {
      console.error('❌ Failed to save recording - Network/Parse error:', error);
      addMessage(`❌ Įrašo išsaugojimo klaida: ${error.message}`);
    }
  };

  return (
    <div className="webrtc-test-container" style={{
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: '20px',
      marginTop: '20px',
      background: '#f8f9fa'
    }}>
      <div className="webrtc-header">
        <h4> WebRTC Balso Testas</h4>
        <p>Testuokite realaus laiko balso sąveiką su AI naudodami savo individualius promptus</p>
      </div>

      <div className="webrtc-status" style={{
        padding: '10px',
        margin: '10px 0',
        borderRadius: '5px',
        background: isConnected ? '#d4edda' : isConnecting ? '#fff3cd' : '#f8d7da',
        color: isConnected ? '#155724' : isConnecting ? '#856404' : '#721c24'
      }}>
        <strong>Būsena:</strong> {status}
      </div>

      <div className="webrtc-info" style={{ marginBottom: '15px', fontSize: '0.9rem', color: '#666' }}>
        {selectedUser && selectedUser !== '' && <div> Testuojama su vartotojo individualiu promptu (ID: {selectedUser})</div>}
        {selectedProfession && selectedProfession !== '' && <div> Testuojama su {selectedProfession} profesijos šablonu</div>}
        {(!selectedUser || selectedUser === '') && (!selectedProfession || selectedProfession === '') && <div> Pasirinkite vartotoją arba profesiją individualiniams promptams</div>}
        <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '5px' }}>
          Debug: selectedUser="{selectedUser}", selectedProfession="{selectedProfession}"
        </div>
      </div>

      <div className="webrtc-controls" style={{ marginBottom: '20px' }}>
        <button
          onClick={connectToAI}
          disabled={isConnecting || isConnected}
          className="btn-primary"
          style={{ marginRight: '10px' }}
        >
          {isConnecting ? ' Jungiamasi...' : ' Prisijungti prie AI'}
        </button>

        <button
          onClick={disconnect}
          disabled={!isConnected && !isConnecting}
          className="btn-secondary"
          style={{ marginRight: '10px' }}
        >
          🔌 Atsijungti
        </button>

        <button
          onClick={clearMessages}
          className="btn-small"
        >
           Išvalyti žurnalą
        </button>
      </div>

      <div className="webrtc-session-info" style={{ marginBottom: '15px' }}>
        {sessionMetrics.sessionId && (
          <>
            <div style={{
              background: '#e8f5e8',
              padding: '10px',
              borderRadius: '4px',
              fontSize: '0.85rem',
              marginBottom: '10px'
            }}>
              <strong> Sesijos statistika:</strong>
              <div>Sesijos ID: {sessionMetrics.sessionId.substring(0, 12)}...</div>
              <div>Žinučių: {sessionMetrics.messageCount} | Funkcijų: {sessionMetrics.functionCalls}</div>
              <div>Kokybė: {(sessionMetrics.quality * 100).toFixed(0)}%</div>
              {sessionMetrics.startTime && (
                <div>Trukmė: {Math.floor((Date.now() - sessionMetrics.startTime.getTime()) / 1000)}s</div>
              )}
            </div>

            {/* Cost & Token Tracking */}
            {(sessionMetrics.totalCostEUR > 0 || sessionMetrics.totalInputTokens > 0) && (
              <div style={{
                background: '#fff3e0',
                padding: '10px',
                borderRadius: '4px',
                fontSize: '0.85rem',
                marginBottom: '10px',
                border: '2px solid #ff9800'
              }}>
                <strong>💰 Išlaidų analizė:</strong>

                <div style={{ marginTop: '8px', fontWeight: 'bold', fontSize: '1.1rem', color: '#e65100' }}>
                  Bendra kaina: €{sessionMetrics.totalCostEUR.toFixed(4)} (${sessionMetrics.totalCostUSD.toFixed(4)})
                </div>

                {sessionMetrics.startTime && sessionMetrics.totalCostEUR > 0 && (
                  <div style={{ marginTop: '5px', color: '#f57c00' }}>
                    Kaina per minutę: €{((sessionMetrics.totalCostEUR / (Math.floor((Date.now() - sessionMetrics.startTime.getTime()) / 1000) / 60)) || 0).toFixed(4)}/min
                  </div>
                )}

                <div style={{ marginTop: '10px', borderTop: '1px solid #ffcc80', paddingTop: '8px' }}>
                  <div><strong>📊 Tokenų sunaudojimas:</strong></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px', marginTop: '5px' }}>
                    <div>
                      <div style={{ color: '#1976d2' }}>🔵 Įėjimas (Input):</div>
                      <div style={{ marginLeft: '15px', fontSize: '0.8rem' }}>
                        Audio: {sessionMetrics.audioInputTokens.toLocaleString()}
                      </div>
                      <div style={{ marginLeft: '15px', fontSize: '0.8rem' }}>
                        Tekstas: {sessionMetrics.textInputTokens.toLocaleString()}
                      </div>
                      <div style={{ marginLeft: '15px', fontWeight: 'bold' }}>
                        Viso: {sessionMetrics.totalInputTokens.toLocaleString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#d32f2f' }}>🔴 Išėjimas (Output):</div>
                      <div style={{ marginLeft: '15px', fontSize: '0.8rem' }}>
                        Audio: {sessionMetrics.audioOutputTokens.toLocaleString()}
                      </div>
                      <div style={{ marginLeft: '15px', fontSize: '0.8rem' }}>
                        Tekstas: {sessionMetrics.textOutputTokens.toLocaleString()}
                      </div>
                      <div style={{ marginLeft: '15px', fontWeight: 'bold' }}>
                        Viso: {sessionMetrics.totalOutputTokens.toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '10px', borderTop: '1px solid #ffcc80', paddingTop: '8px', fontSize: '0.75rem', color: '#666' }}>
                  <div>Kainodara: Audio input $0.032/1K, Audio output $0.064/1K</div>
                  <div>Text input $0.004/1K, Text output $0.016/1K</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {recordingState.isRecording && (
        <div style={{
          background: '#ffebee',
          padding: '10px',
          borderRadius: '4px',
          fontSize: '0.85rem',
          border: '1px solid #ffcdd2',
          marginBottom: '15px'
        }}>
          <strong> Pokalbis įrašomas</strong>
          <div>Pradėta: {recordingState.recordingStartTime?.toLocaleTimeString()}</div>
          <div>Audio fragmentų: {recordingState.audioChunks.length}</div>
        </div>
      )}

      <div className="webrtc-messages">
        <h5> Prisijungimo žurnalas:</h5>
        <div style={{
          maxHeight: '200px',
          overflowY: 'auto',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '10px',
          fontSize: '0.85rem',
          fontFamily: 'monospace'
        }}>
          {messages.length === 0 ? (
            <div style={{ color: '#999', fontStyle: 'italic' }}>Pranešimų dar nėra...</div>
          ) : (
            messages.map((message, index) => (
              <div key={index} style={{ marginBottom: '2px' }}>
                {message}
              </div>
            ))
          )}
        </div>
      </div>

      {conversationHistory.length > 0 && (
        <div className="webrtc-conversation" style={{ marginTop: '15px' }}>
          <h5> Pokalbio istorija:</h5>
          <div style={{
            maxHeight: '150px',
            overflowY: 'auto',
            background: '#f0f8ff',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '10px',
            fontSize: '0.8rem'
          }}>
            {conversationHistory.slice(-5).map((entry, index) => (
              <div key={index} style={{
                marginBottom: '5px',
                padding: '3px',
                background: entry.role === 'user' ? '#e3f2fd' : '#f3e5f5',
                borderRadius: '3px'
              }}>
                <strong>{entry.role === 'user' ? '' : ''}:</strong>
                {entry.content.substring(0, 100)}{entry.content.length > 100 ? '...' : ''}
                {entry.confidence && (
                  <span style={{ fontSize: '0.7rem', color: '#666', marginLeft: '5px' }}>
                    ({(entry.confidence * 100).toFixed(0)}%)
                  </span>
                )}
              </div>
            ))}
            {conversationHistory.length > 5 && (
              <div style={{ textAlign: 'center', fontSize: '0.7rem', color: '#999' }}>
                ... ir dar {conversationHistory.length - 5} žinutės
              </div>
            )}
          </div>
        </div>
      )}

      <div className="webrtc-instructions" style={{
        marginTop: '15px',
        padding: '10px',
        background: '#e7f3ff',
        borderRadius: '4px',
        fontSize: '0.85rem'
      }}>
        <strong> Instrukcijos:</strong>
        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
          <li>Pasirinkite vartotoją arba profesiją aukščiau individualiniams promptams įkelti</li>
          <li>Spustelėkite "Prisijungti prie AI" WebRTC prisijungimui pradėti</li>
          <li>Leiskite mikrofono prieigą, kai paprašys</li>
          <li>Kalbėkite natūraliai - AI atsakys naudodamas jūsų individualius nustatymus</li>
          <li>Stebėkite žurnalą dėl prisijungimo būsenos ir AI atsakymų</li>
        </ul>
      </div>
    </div>
  );
};

export default WebRTCTest;